import './index.less';
import React, { useCallback, useState } from 'react';
import Image from 'next/image';
import { BUNDLE_STORE_MODULE_TYPES } from '@/shared/constants/subscription';
import { BillingOption, PietraPlanModuleType } from '@/shared/types/subscription';
import BundleCard from './BundleCard';
import { StoreModuleItem } from '@/lib/service/pietra-v2';
import { isEmpty } from 'lodash-es';
import { logCommonViewAnonymous } from '@/lib/service';
import router from 'next/router';
import { EventSource } from '@/shared/constants/event';
import { isMobileByUserAgent, scrollTo } from '@/shared/utils/utils';
import { Radio } from 'antd';
import FeaturedIn from '@/components/ExistingBrandsLandingPage/FeaturedIn';
import CompareTable from './CompareTable';
import { PRICING_REVIEWS_CONTENT } from '@/shared/constants/platform-pages-constants';
import RetailConnectReviewsFromOtherBrands from '@/components/Platform/RetailConnect/ReviewsFromOtherBrands';
import NewBrandLogos from './NewBrandLogos';

interface IProps {
  storeModuleItems: StoreModuleItem[];
}

const SimplePricing = ({ storeModuleItems }: IProps) => {
  const [email, setEmail] = useState<string>('');
  const [billingOption, setBillingOption] = useState<BillingOption>(BillingOption.YEAR);
  const handleNavigation = useCallback(
    (e) => {
      e.preventDefault();
      const isMobile = isMobileByUserAgent(navigator?.userAgent);
      email && logCommonViewAnonymous(EventSource.PRICING, email, isMobile);

      router.push(
        '/sell-on-pietra/register?' +
          (email ? 'email=' + email + '&' : '') +
          'event_source=' +
          EventSource.PRICING.toLowerCase(),
      );
    },
    [email],
  );

  const renderHeader = (
    <div className="header">
      <h1 className="title">
        The commerce platform that <span className="gradient">actually helps you run your business.</span>
      </h1>
      <div className="input-wrap">
        <input
          type="email"
          placeholder="Enter your work email"
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleNavigation(e);
            }
          }}
        />
        <button onClick={handleNavigation}>Start free trial</button>
      </div>
    </div>
  );

  const renderSwitch = () => {
    return (
      <Radio.Group
        className="billing-switch"
        optionType="button"
        buttonStyle="solid"
        options={[
          { label: 'Pay monthly', value: BillingOption.MONTH },
          {
            label: (
              <span>
                Pay yearly{' '}
                <span className="saving">
                  save 25%<span className="perks">+perks</span>
                </span>
              </span>
            ),
            value: BillingOption.YEAR,
          },
        ]}
        value={billingOption}
        onChange={(e) => setBillingOption(e.target.value)}
      />
    );
  };

  const renderBundleCards = (
    <div className="bundles">
      {BUNDLE_STORE_MODULE_TYPES.map((moduleType: PietraPlanModuleType, i: number) => (
        <BundleCard key={i} moduleType={moduleType} storeModuleItems={storeModuleItems} billingOption={billingOption} />
      ))}
    </div>
  );

  const renderAskSpecialistButton = (
    <div id="ask-specialist-button" className="ask-specialist-button">
      <Image width={56} height={56} src="/images/blueprint/plan-modules/customer-support.png" alt="Customer support" />
      <div className="right">
        <div className="text">
          <span className="title">Need help deciding what&apos;s best for you?</span>
          <span className="subtitle">Ask a specialist</span>
        </div>
        <div className="online-pill">
          <div className="green-dot" />
          <span>Online</span>
        </div>
      </div>
    </div>
  );

  const showPageContent = !isEmpty(storeModuleItems);

  return (
    <div className="simple-pricing">
      <div className="section">
        {renderHeader}
        {showPageContent && (
          <div className="content">
            <div className="switch-and-logos-wrap">
              <NewBrandLogos />
              {renderSwitch()}
            </div>
            {renderBundleCards}
            <div className="compare-btn">
              <a
                id="compare-btn-for-intercom"
                href="#compare-table-title"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo('compare-table-title');
                }}
              >
                Compare plan features
              </a>
            </div>
            {renderAskSpecialistButton}
          </div>
        )}
      </div>
      {showPageContent && (
        <>
          <RetailConnectReviewsFromOtherBrands
            title="How Pietra makes your business better"
            reviewsData={PRICING_REVIEWS_CONTENT}
          />
          <CompareTable />
          <FeaturedIn />
        </>
      )}
    </div>
  );
};

export default SimplePricing;
